import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';

const Container = styled.div`
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
  background: ${props => props.theme.cardBg};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  transition: all 0.3s ease;
`;

const Title = styled.h1`
  color: ${props => props.theme.primaryText};
  margin-bottom: 1rem;
  transition: color 0.3s ease;
`;

const Subtitle = styled.p`
  color: ${props => props.theme.secondaryText};
  margin-bottom: 2rem;
  transition: color 0.3s ease;
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.primaryText};
  margin-top: 2rem;
  margin-bottom: 1rem;
  transition: color 0.3s ease;
`;

const Paragraph = styled.p`
  color: ${props => props.theme.primaryText};
  line-height: 1.6;
  margin-bottom: 1rem;
  transition: color 0.3s ease;
`;

const List = styled.ul`
  padding-left: 1.5rem;
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.primaryText};
  transition: color 0.3s ease;
`;

const ListItem = styled.li`
  margin-bottom: 0.5rem;
  line-height: 1.6;
`;

const Link = styled.a`
  color: ${props => props.theme.primary};
  text-decoration: none;
  transition: color 0.3s ease;
  
  &:hover {
    text-decoration: underline;
  }
`;

const PrivacyPolicy = () => {
  const { theme } = useTheme();
  
  return (
    <Container theme={theme}>
      <Title theme={theme}>Privacy Policy</Title>
      <Subtitle theme={theme}>Last updated: August 2026</Subtitle>

      <SectionTitle theme={theme}>1. Introduction</SectionTitle>
      <Paragraph theme={theme}>
        This Privacy Policy describes how SlickPicks ("we", "us", or "our") collects, uses, and protects your information when you use our website and services.
      </Paragraph>

      <SectionTitle theme={theme}>2. Information We Collect</SectionTitle>
      <List theme={theme}>
        <ListItem>
          <strong>Account Information:</strong> When you create an account, we collect information such as your name, email address, and password. Passwords are stored in hashed form.
        </ListItem>
        <ListItem>
          <strong>Profile Preferences:</strong> We may store preferences you set in your account, such as preferred sportsbook, email notification settings, and push notification settings.
        </ListItem>
        <ListItem>
          <strong>Tracked and Simulated Picks:</strong> If you use tracking or simulation features, we store related data such as selected picks, odds snapshots, simulated stakes, and simulated bankroll information.
        </ListItem>
        <ListItem>
          <strong>Push Notification Tokens:</strong> If you enable push notifications, we may store device tokens needed to deliver those notifications.
        </ListItem>
        <ListItem>
          <strong>Usage Data:</strong> We may collect information about how you access and use the website, including your IP address, browser type, device information, pages visited, and login activity.
        </ListItem>
        <ListItem>
          <strong>Communications:</strong> If you contact us or submit information through forms on our website (such as a mailing list signup), we collect the information you provide.
        </ListItem>
        <ListItem>
          <strong>Cookies and Similar Technologies:</strong> We use cookies and similar technologies (including local storage) to keep you signed in, remember preferences, and help operate and analyze the Service.
        </ListItem>
      </List>

      <SectionTitle theme={theme}>3. How We Use Your Information</SectionTitle>
      <List theme={theme}>
        <ListItem>To provide, operate, and maintain our website and services, including account features and personalized odds display.</ListItem>
        <ListItem>To communicate with you, including responding to inquiries and sending picks, results, or product updates according to your notification preferences.</ListItem>
        <ListItem>To improve our website, services, models, and user experience.</ListItem>
        <ListItem>To protect the security and integrity of the Service, including preventing abuse and unauthorized access.</ListItem>
        <ListItem>To comply with legal obligations.</ListItem>
      </List>

      <SectionTitle theme={theme}>4. Sharing Your Information</SectionTitle>
      <Paragraph theme={theme}>
        We do not sell, trade, or rent your personal information to third parties. We may share information with trusted service providers who help us operate the Service, such as:
      </Paragraph>
      <List theme={theme}>
        <ListItem>Hosting, cloud infrastructure, and storage providers</ListItem>
        <ListItem>Email delivery providers</ListItem>
        <ListItem>Odds and sports data providers</ListItem>
        <ListItem>Analytics or monitoring providers, if used</ListItem>
      </List>
      <Paragraph theme={theme}>
        These providers are permitted to use your information only to perform services on our behalf. We may also disclose information if required by law or to protect the rights, safety, or integrity of SlickPicks or our users.
      </Paragraph>

      <SectionTitle theme={theme}>5. Third-Party Services</SectionTitle>
      <Paragraph theme={theme}>
        Our website may contain links to third-party websites or services. We are not responsible for the privacy practices or content of those third parties. We encourage you to review their privacy policies.
      </Paragraph>

      <SectionTitle theme={theme}>6. Data Retention</SectionTitle>
      <Paragraph theme={theme}>
        We retain your personal information for as long as your account is active or as needed to provide the Service. If you delete your account or request deletion, we will delete or anonymize your personal information within a reasonable period, except where we must retain certain information to comply with legal obligations, resolve disputes, or enforce our agreements.
      </Paragraph>

      <SectionTitle theme={theme}>7. Your Rights and Choices</SectionTitle>
      <Paragraph theme={theme}>
        Depending on where you live, you may have rights to access, correct, or delete your personal information, or to opt out of certain processing. You can:
      </Paragraph>
      <List theme={theme}>
        <ListItem>Update account details and notification preferences in your profile</ListItem>
        <ListItem>Unsubscribe from marketing or picks/results emails using preference controls or unsubscribe links</ListItem>
        <ListItem>Disable push notifications in your profile or device settings</ListItem>
        <ListItem>Request access to, correction of, or deletion of your personal information by contacting us</ListItem>
      </List>
      <Paragraph theme={theme}>
        If you are a California resident, you may have additional rights under the CCPA/CPRA, including the right to know what personal information we collect and the right to request deletion. We do not sell personal information. To exercise applicable rights, contact us at the email below.
      </Paragraph>

      <SectionTitle theme={theme}>8. Data Security</SectionTitle>
      <Paragraph theme={theme}>
        We implement reasonable security measures to protect your information. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
      </Paragraph>

      <SectionTitle theme={theme}>9. Children's Privacy</SectionTitle>
      <Paragraph theme={theme}>
        Our website is not intended for children under the age of 18. We do not knowingly collect personal information from children under 18. If you believe we have collected such information, please contact us to have it removed.
      </Paragraph>

      <SectionTitle theme={theme}>10. Changes to This Privacy Policy</SectionTitle>
      <Paragraph theme={theme}>
        We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated effective date. Your continued use of the website constitutes your acceptance of the revised policy.
      </Paragraph>

      <SectionTitle theme={theme}>11. Contact Us</SectionTitle>
      <Paragraph theme={theme}>
        If you have any questions about this Privacy Policy, or to make a privacy-related request, please contact us at <Link theme={theme} href="mailto:cx@slickpicks.bet">cx@slickpicks.bet</Link>.
      </Paragraph>
    </Container>
  );
};

export default PrivacyPolicy;
